import * as React from 'react';

import {StackLegacy} from '../layout/stack/stack-legacy';
import {LoadingPage} from '../loading-page/loading-page';

import {Star} from './star';
import {Table} from './table';
import type {CellProps, Cell} from './types';
import {WarningWithPopover} from './warning-with-popover';

import styles from './item-count-table.module.css';

export type Item = {
    bucket: string;
    count: number;
    percent?: number;
    fill: string;
    star?: boolean;
    segments?: any[];
    invalid?: boolean;
};

type Props = {
    items: Item[];
    title: string;
    isLoading?: boolean;
    countColumnTitle?: string;
    includePercentage?: boolean;
    percentageColumnTitle?: string;
    hasHoverState?: boolean;
    disableSorting?: boolean;
    getInvalidPopoverContent?: (bucket: string) => React.ReactElement;
    zeroCountText?: string;
    zeroPercentageText?: string;
};

export function ItemCountTable(props: Props) {
    const {
        title,
        countColumnTitle,
        disableSorting = false,
        hasHoverState = true,
        getInvalidPopoverContent,
        zeroCountText,
        zeroPercentageText,
    } = props;

    const columns = React.useMemo(
        () => [
            {
                id: 'bucket',
                Header: title,
                accessor: (row: Item) => row.bucket,
                dataType: 'component',
                width: 150,
                Cell: (cell: Cell<Item>) => {
                    return (
                        <div className={styles['title-container']}>
                            <div
                                style={{backgroundColor: cell.row.original.fill}}
                                className={styles.dot}
                            ></div>
                            <StackLegacy spacing={6} isHorizontal={true}>
                                {cell.row.original.bucket}
                                {cell.row.original.star ? <Star /> : undefined}
                                {cell.row.original.invalid && getInvalidPopoverContent ? (
                                    <WarningWithPopover
                                        popoverContent={getInvalidPopoverContent(
                                            cell.row.original.bucket
                                        )}
                                    />
                                ) : undefined}
                            </StackLegacy>
                        </div>
                    );
                },
            },
            {
                id: 'count',
                Header: countColumnTitle || 'Count',
                accessor: (row: Item) => row.count,
                dataType: 'integer',
                width: 100,
                minWidth: 100, // override dataType integer default widths
                maxWidth: 100, // override dataType integer default widths
                Cell: ({cell}: CellProps<number>) =>
                    zeroCountText && cell.value === 0 ? zeroCountText : cell.value.toLocaleString(),
            },
        ],
        [title, countColumnTitle, getInvalidPopoverContent, zeroCountText]
    );

    if (props.includePercentage) {
        columns.push({
            id: 'percentage',
            Header: props.percentageColumnTitle || '% of total',
            // @ts-expect-error
            accessor: (row: Item) =>
                zeroPercentageText && row.count === 0 ? zeroPercentageText : row.percent,
            dataType: 'percentage',
            width: 100,
        });
    }

    return (
        <Table
            isResizable={false}
            hasHeaderBorder={true}
            hasHeaderCellBorders={true}
            hasHoverState={hasHoverState}
            isCompact={true}
            stickyHeaders={false}
            hasBorders={true}
            hasCellBorders={false}
            isCheckboxSelectable={false}
            disableFilters={true}
            shouldShowFooter={false}
            // @ts-expect-error
            columns={columns}
            data={props.items}
            disableSorting={disableSorting}
            fullWidth={true}
            specialStates={[
                {
                    shouldRender: () => Boolean(props.isLoading),
                    component: <LoadingPage />,
                },
            ]}
        />
    );
}
